import { Component, OnInit, ViewChild, OnDestroy, HostListener } from '@angular/core';
import { actions, action } from "@models";
import { HttpService, GlobalService, FileUploadService, NotificationService } from '@services';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Editor } from 'primeng/editor';
import { MessageService } from 'primeng/api';
import { 
  notificationXlsx,
  notificationCsv,
  notificationDoc,
  notificationPdf,
  notificationZip,
  defaultProfile,
  profilePicBackground,
  warningIcon,
  sideBarStudentIcon,
  sideBarDriveIcon,
  sideBarEligibilityIcon,
  sideBarEmailTemplateIcon,
  sideBarProgrammeMappingIcon,
  sideBarSettingsIcon,
  republishQueueIcon,
  drivePlannerIcon,
  reminderIcon,
  customMessageIcon,
  metricsIcon,
  chatIcon,
  dashboardIcon,
  reportsIcon
}
from '@assets';
import { Breadcrumb, Eglobal, Severity, Summary, Message } from '@enum';
import { HeaderService } from '@services';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Router } from '@angular/router';
import { StudentService } from '../../services/student.service';
import { DriveService } from '../../services/drive.service';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.css'],
  providers: [MessageService]
})

export class HeaderComponent implements OnInit, OnDestroy{
  @ViewChild('editor') editor!: Editor;
  @ViewChild('searchInput') searchInput: any;
  userData: any = localStorage.getItem('user_details')
  accountData: any = localStorage.getItem('account_details')
  profileFields: FormGroup = new FormGroup({});
  profileDetails: any[] = []
  showNotification: boolean = false;
  spacePattern = /^\s*$/;
  spaceError = /^(\s.*|\s)$/;
  isLoading: boolean = false
  showUnRead: boolean = false
  profileSidebar: boolean = false
  imageChanged: boolean = false
  openNotificationAction: boolean = false
  selectedImage: string = defaultProfile;
  actionOptions: action[] = [
    { icon:'pi-user', label: 'View Profile', value: 'View Profile' },
    { icon:'pi-sign-out', label: 'Logout', value: 'Logout' }
  ]
  dateFormat: string = 'dd-MMM-YYYY hh:mm a';
  profilePicBackground: string = profilePicBackground;
  currentRoute: string = '';
  buttons: any[] = [];
  private unsubscribe$ = new Subject<void>();
  leftButtons: any = [];
  rightButtons: any = [];

  // Search functionality properties
  searchQuery: string = '';
  showSearchDialog: boolean = false;
  filteredModules: any[] = [];
  highlightedIndex: number = 0;
  allModules: any[] = [];
  popularModules: any[] = [];
  searchTimeout: any;

  private searchSubject = new Subject<string>();
  
  searchResults: {
    modules: any[];
    students: any[];
    drives: any[];
  } = {
    modules: [],
    students: [],
    drives: []
  };
  
  // Count of additional results (not shown)
  additionalResults = {
    students: 0,
    drives: 0
  };
  
  isSearching = false;

  constructor(
      public httpService: HttpService, 
      public http: HttpService, 
      private messageService: MessageService, 
      public fileUploadService: FileUploadService,
      public globalService: GlobalService,
      public notificationService: NotificationService,
      public headerService: HeaderService,
      private router: Router,
      private studentService: StudentService,
      private driveService: DriveService
    ) {
    this.userData = JSON.parse(this.userData)
    this.accountData = JSON.parse(this.accountData)
    this.profileDetails = [
      {
        label: 'First Name',
        disable: false,
        type: 'input',
        controlfield: 'first_name',
        mandatory: true,
      },
      {
        label: 'Last Name',
        disable: false,
        type: 'input',
        controlfield: 'last_name',
        mandatory: true,
      },
      {
        label: 'Email',
        disable: true,
        type: 'input',
        controlfield: 'primary_email',
        mandatory: true,
      }
    ];
    this.notificationService.requestNotificationsPermissions();
    this.notificationService.fetchNotifications();
    
    // Setup debounced search
    this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(query => {
      if (query && query.length >= 2) {
        this.performSearch(query);
      } else {
        this.clearSearchResults();
      }
    });
  }

  async ngOnInit() {
    this.headerService.headerState$
    .pipe(takeUntil(this.unsubscribe$))
    .subscribe(state => {
      this.currentRoute = state.currentRoute;
      this.buttons = state.buttons;
      this.leftButtons = this.buttons.filter(button => button?.position === 'left');
      this.rightButtons = this.buttons.filter(button => button?.position === 'right');
    });
    this.profileFields = this.createProfileFormGroup();
    if(this.userData.profile_picture) {
      const signedUrl = await this.globalService.getSignedURL({ bucketName: this.globalService.publicBucket, fileName: this.userData.profile_picture });
      this.selectedImage = signedUrl.data.response;
    };

    // Listen for custom event from sidebar to open profile
    window.addEventListener('openProfileSidebar', (event: any) => {
      this.openProfileFromSidebar(event.detail.userData);
    });

    // Initialize search modules
    this.initializeSearchModules();
  }

  initializeSearchModules() {
    this.allModules = [
      {
        label: 'Students',
        route: 'students',
        icon: sideBarStudentIcon,
        description: 'Manage student profiles and information',
        keywords: ['students', 'student', 'profile', 'manage', 'users', 'people']
      },
      {
        label: 'Drives',
        route: 'drives',
        icon: sideBarDriveIcon,
        description: 'Manage placement drives and job opportunities',
        keywords: ['drives', 'jobs', 'placement', 'opportunities', 'career', 'hiring']
      },
      {
        label: 'Drive Planner',
        route: 'planner',
        icon: drivePlannerIcon,
        description: 'Plan and schedule placement drives',
        keywords: ['planner', 'schedule', 'planning', 'calendar', 'timeline']
      },
      {
        label: 'Eligibility',
        route: 'eligibility',
        icon: sideBarEligibilityIcon,
        description: 'Check student eligibility for drives',
        keywords: ['eligibility', 'check', 'qualify', 'criteria', 'requirements']
      },
      {
        label: 'Email Templates',
        route: 'email-template',
        icon: sideBarEmailTemplateIcon,
        description: 'Manage email templates and communications',
        keywords: ['email', 'template', 'communication', 'mail', 'message']
      },
      {
        label: 'Email Insights',
        route: 'email-insights',
        icon: customMessageIcon,
        description: 'Analytics and insights for email campaigns',
        keywords: ['insights', 'analytics', 'email analytics', 'campaign', 'metrics']
      },
      {
        label: 'Whatsapp',
        route: 'whatsapp',
        icon: chatIcon,
        description: 'Real-time chat and messaging system',
        keywords: ['chat', 'message', 'communication', 'talk', 'conversation', 'whatsapp']
      },
      {
        label: 'Engagement Metrics',
        route: 'engagement-metrics',
        icon: metricsIcon,
        description: 'Track user engagement and performance metrics',
        keywords: ['metrics', 'engagement', 'performance', 'analytics', 'tracking']
      },
      {
        label: 'Reports',
        route: 'reports',
        icon: reportsIcon,
        description: 'Generate and view various reports',
        keywords: ['reports', 'reporting', 'generate', 'view', 'data']
      },
      {
        label: 'Reminders',
        route: 'reminders',
        icon: reminderIcon,
        description: 'Manage reminders and notifications',
        keywords: ['reminders', 'notifications', 'alerts', 'remind', 'notify']
      },
      {
        label: 'Republish Queue',
        route: 'republish-queue',
        icon: republishQueueIcon,
        description: 'Manage content republishing queue',
        keywords: ['republish', 'queue', 'content', 'publish', 'manage']
      },
      {
        label: 'Programme Mapping',
        route: 'programme-mapping',
        icon: sideBarProgrammeMappingIcon,
        description: 'Map and manage academic programmes',
        keywords: ['programme', 'mapping', 'academic', 'courses', 'curriculum']
      },
      {
        label: 'Settings',
        route: 'settings',
        icon: sideBarSettingsIcon,
        description: 'Configure system settings and preferences',
        keywords: ['settings', 'configuration', 'preferences', 'setup', 'options']
      },
      // Inner Settings Tabs
      {
        label: 'College Profile',
        route: 'settings',
        icon: sideBarSettingsIcon,
        description: 'Manage college profile and branding settings',
        keywords: ['college profile', 'college', 'profile', 'branding', 'logo', 'domain', 'email signature'],
        settingsTab: 'college_profile'
      },
      {
        label: 'Student Fields',
        route: 'settings',
        icon: sideBarSettingsIcon,
        description: 'Configure student field mappings and requirements',
        keywords: ['student fields', 'fields', 'field mapping', 'student info', 'requirements'],
        settingsTab: 'student_fields'
      },
      {
        label: 'Account Settings',
        route: 'settings',
        icon: sideBarSettingsIcon,
        description: 'Manage account and user settings',
        keywords: ['account settings', 'account', 'user settings', 'password', 'email'],
        settingsTab: 'account_settings'
      },
      {
        label: 'Platform Settings',
        route: 'settings',
        icon: sideBarSettingsIcon,
        description: 'Configure platform-wide settings and preferences',
        keywords: ['platform settings', 'platform', 'system settings', 'general', 'labeling', 'download format'],
        settingsTab: 'platform_settings'
      },
      {
        label: 'Edit Fields',
        route: 'settings',
        icon: sideBarSettingsIcon,
        description: 'Edit and customize field configurations',
        keywords: ['edit fields', 'edit', 'customize', 'field configuration', 'attributes'],
        settingsTab: 'edit_fields'
      },
      {
        label: 'Placement Policy',
        route: 'settings',
        icon: sideBarSettingsIcon,
        description: 'Manage placement policies and guidelines',
        keywords: ['placement policy', 'policy', 'placement', 'guidelines', 'rules'],
        settingsTab: 'placement_policy'
      }
    ];

    this.popularModules = [
      this.allModules.find(m => m.route === 'dashboard'),
      this.allModules.find(m => m.route === 'students'),
      this.allModules.find(m => m.route === 'drives'),
      this.allModules.find(m => m.route === 'email-template'),
      this.allModules.find(m => m.route === 'reports'),
      this.allModules.find(m => m.route === 'settings')
    ].filter(Boolean);
  }

  onSearchInput() {
    this.searchSubject.next(this.searchQuery);
  }

  onSearchKeydown(event: KeyboardEvent) {
    if (this.filteredModules.length === 0) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.highlightedIndex = (this.highlightedIndex + 1) % this.filteredModules.length;
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.highlightedIndex = this.highlightedIndex === 0 ? this.filteredModules.length - 1 : this.highlightedIndex - 1;
        break;
      case 'Enter':
        event.preventDefault();
        if (this.filteredModules[this.highlightedIndex]) {
          this.navigateToModule(this.filteredModules[this.highlightedIndex]);
        }
        break;
      case 'Escape':
        this.closeSearchDialog();
        break;
    }
  }

  clearSearch() {
    this.searchQuery = '';
    this.filteredModules = [];
    this.highlightedIndex = 0;
  }

  tabChange(button: any, value: string) {
    button.currentValue = value;
    button.action({value});
  }

  getProfileImage(student: any): string | null {
    if (student['profile_picture']) {
      return `${this.globalService.prefix}/${this.globalService.publicBucket}/${student['profile_picture']}`;
    } else if (student['company_logo']) {
      return `${student['company_logo']}`;
    } else {
      return null;
    }
  }

  getProfileColor(student: any): string {
    const colors = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7', '#DDA0DD', '#98D8C8', '#F7DC6F'];
    const email = student.primary_email || student.email || '';
    const hash = email.split('').reduce((a: number, b: string) => {
      a = ((a << 5) - a) + b.charCodeAt(0);
      return a & a;
    }, 0);
    return colors[Math.abs(hash) % colors.length];
  }

  getProfileTextColor(student: any): string {
    return '#FFFFFF';
  }

  getProfileInitial(student: any): string {
    const email = student.primary_email || student.email || '';
    const firstName = student.first_name || '';
    
    if (email && /^[a-zA-Z]/.test(email)) {
      return email.charAt(0).toUpperCase();
    }
    
    if (firstName) {
      return firstName.charAt(0).toUpperCase();
    }
    
    return '?';
  }

  onProfileImgError(event: any) {
    event.target.style.display = 'none';
    const fallbackAvatar = event.target.parentElement.querySelector('.profile-fallback-avatar');
    if (fallbackAvatar) {
      fallbackAvatar.style.display = 'flex';
    }
  }

  filterModules() {
    if (!this.searchQuery.trim()) {
      this.filteredModules = [];
      return;
    }

    const query = this.searchQuery.toLowerCase().trim();
    this.filteredModules = this.allModules.filter(module => {
      return (
        module.label.toLowerCase().includes(query) ||
        module.route.toLowerCase().includes(query) ||
        module.description.toLowerCase().includes(query) ||
        module.keywords.some((keyword: string) => keyword.toLowerCase().includes(query))
      );
    });

    this.highlightedIndex = this.filteredModules.length > 0 ? 0 : -1;
  }

  navigateToModule(module: any) {
    if (module.route === 'settings' && module.settingsTab) {
      this.router.navigate(['/settings'], { queryParams: { tab: module.settingsTab } });
    } else {
      this.router.navigateByUrl(module.route);
    }
    this.closeSearchDialog();
  }

  openSearchDialog() {
    this.showSearchDialog = true;
    this.clearSearch();
    setTimeout(() => {
      this.searchInput?.nativeElement?.focus();
    }, 100);
  }

  closeSearchDialog() {
    this.showSearchDialog = false;
    this.clearSearch();
  }

  @HostListener('document:keydown', ['$event'])
  handleKeyboardNavigation(event: KeyboardEvent) {
    // Handle Ctrl+K to open search dialog
    if (event.ctrlKey && event.key === 'k') {
      event.preventDefault();
      this.openSearchDialog();
      return;
    }
  }

  createProfileFormGroup() {
    const formGroupConfig: any = {};
    this.profileDetails.forEach((data) => {
      formGroupConfig[data.controlfield] = new FormControl(
        '',
        data.mandatory ? Validators.required : null
      );
    });
    return new FormGroup(formGroupConfig);
  }

  openNotification(){
    this.showNotification = true;
  }

  openProfileFromSidebar(userData: any) {
    // Update user data if provided
    if (userData) {
      this.userData = userData;
    }
    
    // Reset form and open profile sidebar
    this.profileFields.patchValue(this.userData);
    this.imageChanged = false;
    this.profileSidebar = true;
  }

  closeDialog() {
    this.openNotificationAction = false;
    this.showNotification = false;
  }

  async markAllAsRead() {
    this.isLoading = true;
    this.notificationService.notificationData.forEach((item) => {
      item.status = 'read';
    });
    this.notificationService.notificationCount = 0;
    await this.httpService.post('/notifications/update', { data: this.notificationService.notificationData });
    this.isLoading = false;
  }

  onClickAction(options: actions){
    this.openNotificationAction = false; 
  }

  async onEditProfile() {
    if (this.isLoading) return;
  
    const payload = this.constructPayload();
    if (!payload) return;
  
    if (this.imageChanged) {
      const imageUploadSuccess = await this.uploadProfileImage(payload);
      if (!imageUploadSuccess) return;
    }
  
    if (!Object.keys(payload).length) {
      this.showWarningMessage(Message.UNCHANGED);
      return;
    }
  
    this.isLoading = true;
  
    const res = await this.httpService.put('/admins/me', payload);
    this.handleProfileUpdateResponse(res, payload);
  }
  
  private constructPayload() {
    let payload: any = {};
    const userData = JSON.parse(localStorage.getItem('user_details') ?? '');
  
    for (let fields in this.profileFields.controls) {
      if (this.profileFields.controls[fields].value !== userData[fields]) {
        payload[fields] = this.profileFields.controls[fields].value;
      }
    }
  
    if (this.spacePattern.test(payload.first_name) || this.spaceError.test(payload.first_name)) {
      this.showErrorMessage(Message.VALIDFIRSTNAME);
      return null;
    } else if (this.spacePattern.test(payload.last_name) || this.spaceError.test(payload.last_name)) {
      this.showErrorMessage(Message.VALIDLASTNAME);
      return null;
    }
  
    return payload;
  }
  
  private async uploadProfileImage(payload: any) {
    const type: any = this.selectedImage.split(';base64')[0].split(':')[1];
    let blobData: any = this.globalService.dataURItoBlob(this.selectedImage, type);
    let pay = {
      bucketName: this.globalService.publicBucket,
      fileName: `user_image/${this.accountData.group_account_id}/${this.userData.user_id}/${new Date().getTime()}`,
      type: type,
    };
    const s3SignedUrl = await this.globalService.getSignedUploadUrl(pay);
    let s3Response: any = await this.fileUploadService.uploadUsingSignedUrl(s3SignedUrl['data'].response, blobData);
    
    if (s3Response && s3Response.status !== 200) {
      this.showErrorMessage(Message.PROFILEUPDATEFAILED);
      this.profileSidebar = false;
      this.isLoading = false;
      return false;
    }
  
    payload.profile_picture = pay.fileName;
    return true;
  }
  
  private handleProfileUpdateResponse(res: any, payload: any) {
    if (res?.success && res?.data) {
      const { data } = res;
      this.showSuccessMessage(Message.PROFILEUPDATE);
      this.profileSidebar = false;
      this.isLoading = false;
      this.userData = { ...data };
      this.updateLocalStorage(data);
      
      // Notify sidebar component about profile update
      const event = new CustomEvent('profileUpdated', {
        detail: { userData: data }
      });
      window.dispatchEvent(event);
    } else {
      this.showErrorMessage(Message.PROFILEUPDATEFAILED);
      this.isLoading = false;
      this.profileSidebar = false;
    }
  }
  
  private showErrorMessage(detail: string) {
    this.messageService.add({
      severity: Severity.ERROR,
      summary: Summary.ERROR,
      detail: detail,
    });
  }
  
  private showWarningMessage(detail: string) {
    this.messageService.add({
      severity: Severity.WARN,
      summary: Summary.VALIDATION,
      detail: detail,
    });
  }
  
  private showSuccessMessage(detail: string) {
    this.messageService.add({
      severity: Severity.SUCCESS,
      summary: Summary.SUCCESS,
      detail: detail,
    });
  }
  
  private updateLocalStorage(data: any) {
    const localStorageData = JSON.parse(localStorage.getItem('user_details') ?? '');
    localStorageData.first_name = data.first_name;
    localStorageData.last_name = data.last_name;
    localStorageData.profile_picture = data.profile_picture;
    localStorage.setItem('user_details', JSON.stringify(localStorageData));
  }

  onFileSelected(event: any, purpose: any): void {
    if (event?.target?.files?.[0]) {
      this.imageChanged = true;
      const file = event.target.files[0];
      const reader = new FileReader();

      reader.onload = (e: any) => {
        this.selectedImage = e.target.result;
      };
      reader.readAsDataURL(file);
    }
  }

  onClick(options: actions){
    if (options.label === Eglobal.VIEWPROFILE) {
      this.userData = JSON.parse(localStorage.getItem('user_details') ?? '')
      this.profileFields.patchValue(this.userData)
      this.imageChanged = false
      this.profileSidebar = true
      return;
    }
    this.globalService.resetLogin();
  }

  getIconSrc(extension: string): any {
    if (extension?.includes('.xlsx')) {
      return notificationXlsx;
    } else if (extension?.includes('.zip')) {
      return notificationZip;
    } else if (extension?.includes('.csv')) {
      return notificationCsv;
    } else if (extension?.includes('.pdf')) {
      return notificationPdf; 
    } else if (extension?.includes('.docx')) {
      return notificationDoc; 
    } else {
      return warningIcon;
    }
  }  

  onOpenDialog() {
    this.openNotificationAction = !this.openNotificationAction;
  }

  async fileAction(item: any) {
    await this.httpService.post('/notifications/update', {
      data: [item]
    });
    if(item.status === 'unread') {
      item.status = 'read';
      this.notificationService.notificationCount -= 1;
    };
    const urls = new URL(item.s3link);
    const objectKey = decodeURIComponent(urls.pathname.substring(1));
    let relativePath = objectKey.replace(/^.*?\//, '');
    const filePath = relativePath;
    const bucketData = {
      bucketName: this.globalService.bucketName,
      fileName: filePath,
    };
    
    const url = await this.globalService.getSignedURL(bucketData);

    window.open(url.data.response, '_blank');
  }

  async deleteNotification(item: any) {
    this.notificationService.deleteNotification(item);
  };

  async unreadOnly() {
    this.notificationService.unreadOnly(this.showUnRead);
    this.showUnRead = !this.showUnRead;
  };

  async onCloseSideBar() {
    this.profileSidebar = false;
    const signedUrl = await this.globalService.getSignedURL({ bucketName: this.globalService.publicBucket, fileName: this.userData.profile_picture });
    this.selectedImage = signedUrl.data.response;
  }

  async performSearch(query: string) {
    this.isSearching = true;
    
    try {
      // Search modules (local)
      const moduleResults = this.allModules.filter(module => 
        module.label.toLowerCase().includes(query.toLowerCase().trim()) ||
        module.description.toLowerCase().includes(query.toLowerCase().trim()) ||
        module.route.toLowerCase().includes(query.toLowerCase().trim())
      );
      
      // Search students and drives (API calls)
      const [studentResults, driveResults] = await Promise.all([
        this.searchStudents(query),
        this.searchDrives(query)
      ]);
      
      this.searchResults = {
        modules: moduleResults.slice(0, 3),
        students: studentResults.slice(0, 3),
        drives: driveResults.slice(0, 3)
      };
      
      this.additionalResults = {
        students: Math.max(0, studentResults.length - 3),
        drives: Math.max(0, driveResults.length - 3)
      };
      
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      this.isSearching = false;
    }
  }
  
  searchModules(query: string) {
    return this.allModules.filter(module => 
      module.label.toLowerCase().includes(query.toLowerCase()) ||
      module.description.toLowerCase().includes(query.toLowerCase()) ||
      module.route.toLowerCase().includes(query.toLowerCase())
    );
  }
  
  async searchStudents(query: string) {
    try {
      const payload = {
        limit: 10,
        page: 1,
        search: query,
        search_by: 'email',
        filters: {
          profileStatus: [],
          platformStatus: [],
          driveStatus: [],
          interestedForPlacement: [],
          pass_out_year: [],
          degree: [],
          department: [],
          campus: []
        },
        fields: ['primary_email', 'first_name', 'last_name', 'profile_picture', 'middle_name', 'profile_status'],
        sort_by: 'created_at',
        sort_order: 2,
        group_account_id: JSON.parse(localStorage.getItem('account_details') ?? '{}').group_account_id
      };
      
      const response = await this.studentService.fetchStudentDetails(payload);
      if (response.success) {
        return response.data.data.map((student: any) => ({
          type: 'student',
          id: student.id,
          label: student?.first_name ? (student?.first_name + ' ' + (student?.middle_name ? student.middle_name + ' ' : '') + student?.last_name) : '--',
          description: student.primary_email,
          route: `/students/profile?id=${student.primary_email}`,
          email: student.primary_email,
          status: student.profile_status,
          ...(student?.profile_picture ? { profile_picture: student?.profile_picture } : {})
        }));
      }
    } catch (error) {
      console.error('Student search error:', error);
    }
    return [];
  }
  
  async searchDrives(query: string) {
    try {
      const payload = {
        limit: 10,
        page: 1,
        driveObjective: '',
        filters: {
          drive_status: [],
          pass_out_year: [],
          campus: [],
          department: [],
          degree: [],
          createdAt: []
        },
        search: query,
        sort_by: 'created_at',
        sort_order: 2
      };
      
      const response = await this.driveService.filterDriveData(payload, 'drive');
      if (response.success) {
        return response.data.data.data.map((drive: any) => ({
          type: 'drive',
          id: drive._id,
          label: `${drive.name} - ${drive.company_name}`,
          description: drive.drive_objective || 'No description available',
          route: `/drives/drive-details?id=${drive._id}`,
          status: drive.drive_status,
          ...(drive?.company_logo ? { company_logo: drive?.company_logo } : {})
        }));
      }
    } catch (error) {
      console.error('Drive search error:', error);
    }
    return [];
  }
  
  clearSearchResults() {
    this.searchResults = {
      modules: [],
      students: [],
      drives: []
    };
    this.additionalResults = {
      students: 0,
      drives: 0
    };
  }
  
  getTotalResults() {
    return this.searchResults.modules.length + 
           this.searchResults.students.length + 
           this.searchResults.drives.length;
  }

  onToggleChange(event: any, button: any) {
    const isChecked = (event.target as HTMLInputElement).checked;
    const value = isChecked ? 'Internship' : 'Placement';
    button?.action({ value });
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    // Remove event listeners
    window.removeEventListener('openProfileSidebar', this.openProfileFromSidebar.bind(this));
  }
}
